"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Search, ArrowRight, Home, MapPin, TrendingUp, Building, ShieldCheck } from "lucide-react";
function WidgetHeader({
  title
}: {
  title: string;
}) {
  return <h3 className="text-[18px] font-sans font-semibold text-foreground mb-6 relative pl-4 flex items-center tracking-tight">
      {}
      <span className="absolute left-0 top-1 bottom-1 w-[2px] bg-accent"></span>
      <span className="absolute left-0 bottom-1 h-[2px] w-3 bg-accent"></span>
      {title}
    </h3>;
}
function RecentPostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="159245" className="flex flex-col gap-6">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-2 animate-pulse">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-4/5"></div>
            <div className="h-3 bg-muted rounded w-1/3 mt-1"></div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <p className="text-[0.9rem] text-muted-foreground font-sans">No recent health updates available.</p>;
  }
  return <div className="flex flex-col gap-6">
      {posts.map((post, i) => <div key={post.id} data-index={i} className="flex flex-col gap-2 group">
          <Link to={post.link || "#"} className="text-[0.9rem] font-sans font-medium text-secondary group-hover:text-primary transition-colors duration-300 leading-snug line-clamp-2">
            {post.title}
          </Link>
          <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-widest">
            {post.date || "Latest"}
          </span>
        </div>)}
    </div>;
}
export default function SidebarWidgets() {
  const wp_query_params = {
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  const categories = [{
    name: "Spine Health",
    url: "/HealthInsightsPage",
    icon: Home
  }, {
    name: "Recovery Tips",
    url: "/TreatmentsPage",
    icon: TrendingUp
  }, {
    name: "Clinical Updates",
    url: "/ConditionsPage",
    icon: ShieldCheck
  }, {
    name: "Patient Success",
    url: "/AboutBodywerksPage",
    icon: MapPin
  }, {
    name: "Wellness Blog",
    url: "/OurStoryPage",
    icon: Building
  }];
  return <section id="categories-sidebar" className="w-full flex flex-col gap-10 bg-background p-6 lg:p-8 rounded-lg border border-border/40 shadow-sm">
      
      {}
      <div className="widget-container">
        <WidgetHeader title="Find Health Advice" />
        <div className="relative flex items-center">
          <input type="text" placeholder="Search conditions or treatments..." className="w-full h-12 pl-4 pr-12 rounded-md border border-input bg-card text-[0.9rem] focus:outline-none focus:ring-2 focus:ring-accent/50 transition-shadow font-sans" />
          <Link to="/HealthInsightsPage" className="absolute right-1">
            <Button size="icon" variant="ghost" className="h-10 w-10 text-secondary hover:text-accent hover:bg-accent/10">
              <Search className="w-5 h-5" />
              <span className="sr-only">Search</span>
            </Button>
          </Link>
        </div>
      </div>

      {}
      <div className="widget-container">
        <WidgetHeader title="Explore Topics" />
        <ul className="flex flex-col gap-4">
          {categories.map((cat, i) => <li key={i} data-index={i} className="flex items-center">
              <Link to={cat.url} className="group relative inline-flex items-center gap-3 text-[0.9rem] font-sans font-medium text-secondary hover:text-primary transition-colors duration-300 py-1 w-full tracking-wider">
                <cat.icon className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                <span>{cat.name}</span>
                {}
                <span className="absolute left-7 bottom-0 w-[calc(100%-28px)] h-[2px] bg-accent scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"></span>
              </Link>
            </li>)}
        </ul>
      </div>

      {}
      <div className="widget-container">
        <WidgetHeader title="Latest Insights" />
        <PostsProvider wp_query={{
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <RecentPostsConsumer />
        </PostsProvider>
      </div>

      {}
      <div className="widget-container mt-4">
        <div className="bg-primary text-primary-foreground p-8 rounded-lg relative overflow-hidden group shadow-md border border-primary/10">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/60 to-primary/95"></div>
          
          <div className="relative z-10 flex flex-col gap-5">
            <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-accent/30">
              <ShieldCheck className="w-6 h-6 text-accent" />
            </div>
            
            <div>
              <h4 className="text-xl font-sans font-bold mb-2 leading-tight">Free Spinal Assessment?</h4>
              <p className="text-[0.9rem] text-primary-foreground/80 leading-relaxed font-sans">
                Take the first step toward lasting relief with a professional evaluation at our Cyprus clinics.
              </p>
            </div>
            
            <Link to="/ContactPage">
              <Button variant="secondary" className="w-full mt-2 group/btn h-12 text-[0.9rem] font-medium tracking-wide">
                Book Assessment
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

    </section>;
}